import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Lock,
  LogOut,
  Users,
  CheckCircle2,
  XCircle,
  UserCheck,
  Phone,
  MessageSquare,
  Calendar,
  Loader2,
  Trash2,
  AlertTriangle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "ff_admin_pw";

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function LoginGate({ onUnlock }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { password: pw });
      sessionStorage.setItem(STORAGE_KEY, pw);
      onUnlock(pw);
    } catch (e) {
      setErr("Incorrect password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100svh] flex items-center justify-center px-6 bg-cotton">
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="glass-card rounded-[28px] p-8 w-full max-w-sm"
        data-testid="admin-login-form"
      >
        <div className="flex items-center justify-center w-14 h-14 rounded-full bg-champagne/15 mx-auto mb-5">
          <Lock size={20} className="text-champagne" strokeWidth={1.6} />
        </div>
        <h1 className="font-serif italic text-3xl text-ink text-center">
          Admin
        </h1>
        <p className="text-xs uppercase tracking-[0.35em] text-ink/50 text-center mt-1">
          Fuzail &amp; Fayeza
        </p>

        <label className="block mt-7 text-[10px] uppercase tracking-[0.35em] text-ink/55 mb-2 pl-1">
          Password
        </label>
        <input
          type="password"
          autoFocus
          data-testid="admin-pw-input"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          placeholder="••••••••"
          className="wedding-input"
        />
        {err && (
          <p
            className="text-xs text-red-500 mt-2 pl-1"
            data-testid="admin-pw-error"
          >
            {err}
          </p>
        )}

        <button
          type="submit"
          disabled={loading || !pw}
          data-testid="admin-pw-submit"
          className="w-full mt-5 rounded-full bg-ink text-cotton py-3.5 text-xs uppercase tracking-[0.3em] disabled:opacity-60 transition-all hover:bg-ink/90"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 size={14} className="animate-spin" />
              Checking
            </span>
          ) : (
            "Enter"
          )}
        </button>
      </motion.form>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div
      className="glass-card rounded-2xl p-5 flex flex-col items-start gap-2"
      data-testid={testid}
    >
      <div className="w-9 h-9 rounded-full bg-champagne/15 flex items-center justify-center">
        <Icon size={16} className="text-champagne" strokeWidth={1.6} />
      </div>
      <p className="text-[10px] uppercase tracking-[0.3em] text-ink/55">
        {label}
      </p>
      <p className="font-serif italic font-light text-3xl text-ink leading-none">
        {value}
      </p>
    </div>
  );
}

function Dashboard({ password, onLogout }) {
  const [rsvps, setRsvps] = useState(null);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [reloadCount, setReloadCount] = useState(0);
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);

  const headers = { "X-Admin-Password": password };

  useEffect(() => {
    let cancelled = false;
    setError("");
    Promise.all([
      axios.get(`${API}/rsvp`, { headers }),
      axios.get(`${API}/rsvp/stats`, { headers }),
    ])
      .then(([r, s]) => {
        if (cancelled) return;
        setRsvps(r.data);
        setStats(s.data);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e.response?.status === 401) {
          sessionStorage.removeItem(STORAGE_KEY);
          onLogout();
        } else {
          setError("Failed to load. Try again.");
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password, reloadCount, onLogout]);

  const reload = () => setReloadCount((c) => c + 1);

  const deleteOne = async (id) => {
    if (!window.confirm("Delete this RSVP? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/rsvp/${id}`, { headers });
      reload();
    } catch {
      setError("Failed to delete. Try again.");
    }
  };

  const clearAll = async () => {
    setClearing(true);
    try {
      await axios.delete(`${API}/rsvp/all`, { headers });
      setConfirmClear(false);
      reload();
    } catch {
      setError("Failed to clear. Try again.");
    } finally {
      setClearing(false);
    }
  };

  const filtered = (rsvps || []).filter((r) => {
    if (filter === "yes") return r.attending === "yes";
    if (filter === "no") return r.attending === "no";
    return true;
  });

  return (
    <div className="min-h-[100svh] bg-cotton text-ink">
      {/* Header */}
      <header className="border-b border-champagne/20 px-5 py-4 flex items-center justify-between bg-cotton/80 backdrop-blur-sm sticky top-0 z-20">
        <div>
          <p className="text-[10px] uppercase tracking-[0.4em] text-ink/55">
            Admin
          </p>
          <h1 className="font-serif italic text-xl text-ink leading-none">
            Fuzail &amp; Fayeza
          </h1>
        </div>
        <button
          type="button"
          onClick={onLogout}
          data-testid="admin-logout"
          className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-ink/65 border border-champagne/40 rounded-full px-3 py-2 hover:bg-ink hover:text-cotton hover:border-ink transition-all"
        >
          <LogOut size={12} strokeWidth={1.6} />
          Logout
        </button>
      </header>

      <main className="px-5 py-6 max-w-2xl mx-auto">
        {/* Stats */}
        <section className="grid grid-cols-2 gap-3" data-testid="admin-stats">
          <StatCard
            icon={Users}
            label="Total"
            value={stats?.total_responses ?? "—"}
            testid="stat-total"
          />
          <StatCard
            icon={UserCheck}
            label="Guests Coming"
            value={stats?.total_guests_attending ?? "—"}
            testid="stat-guests"
          />
          <StatCard
            icon={CheckCircle2}
            label="Attending"
            value={stats?.attending ?? "—"}
            testid="stat-attending"
          />
          <StatCard
            icon={XCircle}
            label="Declined"
            value={stats?.not_attending ?? "—"}
            testid="stat-declined"
          />
        </section>

        {/* Filters + Clear All */}
        <section className="mt-7 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            {[
              { v: "all", label: "All" },
              { v: "yes", label: "Attending" },
              { v: "no", label: "Declined" },
            ].map((opt) => {
              const active = filter === opt.v;
              return (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setFilter(opt.v)}
                  data-testid={`filter-${opt.v}`}
                  className={`text-[10px] uppercase tracking-[0.3em] px-3 py-2 rounded-full border transition-all ${
                    active
                      ? "bg-ink text-cotton border-ink"
                      : "bg-white/40 text-ink/65 border-champagne/30 hover:border-champagne/70"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          {rsvps && rsvps.length > 0 && (
            <button
              type="button"
              onClick={() => setConfirmClear(true)}
              data-testid="clear-all-btn"
              className="text-[10px] uppercase tracking-[0.3em] px-3 py-2 rounded-full border border-red-300 text-red-600 hover:bg-red-600 hover:text-white hover:border-red-600 transition-all flex items-center gap-1.5"
            >
              <Trash2 size={11} strokeWidth={1.8} />
              Clear All
            </button>
          )}
        </section>

        {/* Confirm clear modal */}
        <AnimatePresence>
          {confirmClear && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center px-6 bg-ink/40 backdrop-blur-sm"
              data-testid="confirm-clear-modal"
            >
              <motion.div
                initial={{ scale: 0.9, y: 10 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="glass-card rounded-3xl p-7 max-w-sm w-full text-center"
              >
                <div className="w-12 h-12 mx-auto rounded-full bg-red-50 flex items-center justify-center">
                  <AlertTriangle
                    size={20}
                    className="text-red-500"
                    strokeWidth={1.6}
                  />
                </div>
                <h3 className="font-serif italic text-2xl text-ink mt-4">
                  Clear all RSVPs?
                </h3>
                <p className="text-sm text-ink/65 mt-2 leading-relaxed">
                  This will permanently delete all {rsvps?.length || 0}{" "}
                  responses. This action cannot be undone.
                </p>

                <div className="mt-6 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setConfirmClear(false)}
                    disabled={clearing}
                    data-testid="confirm-cancel"
                    className="flex-1 rounded-full border border-champagne/40 text-ink py-3 text-xs uppercase tracking-[0.25em] hover:bg-ink/5 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={clearAll}
                    disabled={clearing}
                    data-testid="confirm-clear-yes"
                    className="flex-1 rounded-full bg-red-600 text-white py-3 text-xs uppercase tracking-[0.25em] hover:bg-red-700 disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
                  >
                    {clearing ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Trash2 size={12} strokeWidth={1.8} />
                    )}
                    Delete All
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* List */}
        <section className="mt-5 space-y-3" data-testid="rsvp-list">
          {error && (
            <p className="text-sm text-red-500 text-center py-6">{error}</p>
          )}
          {!rsvps && !error && (
            <div className="flex items-center justify-center py-12 text-ink/55">
              <Loader2 size={18} className="animate-spin" />
            </div>
          )}
          {rsvps && filtered.length === 0 && (
            <p className="text-center text-sm text-ink/55 py-10 font-serif italic">
              No RSVPs yet.
            </p>
          )}
          <AnimatePresence>
            {filtered.map((r, i) => (
              <motion.article
                key={r.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4, delay: i * 0.03 }}
                className="glass-card rounded-2xl p-5"
                data-testid={`rsvp-item-${r.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-serif italic text-xl text-ink leading-tight">
                      {r.name}
                    </h3>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-ink/45 mt-1 flex items-center gap-1.5">
                      <Calendar size={11} strokeWidth={1.6} />
                      {formatDate(r.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span
                      className={`text-[10px] uppercase tracking-[0.25em] rounded-full px-2.5 py-1 ${
                        r.attending === "yes"
                          ? "bg-champagne/20 text-ink border border-champagne/40"
                          : "bg-ink/8 text-ink/60 border border-ink/20"
                      }`}
                    >
                      {r.attending === "yes"
                        ? `Yes · ${r.guests}`
                        : "No"}
                    </span>
                    <button
                      type="button"
                      onClick={() => deleteOne(r.id)}
                      data-testid={`delete-rsvp-${r.id}`}
                      aria-label="Delete RSVP"
                      className="text-ink/40 hover:text-red-600 transition-colors p-1"
                    >
                      <Trash2 size={14} strokeWidth={1.6} />
                    </button>
                  </div>
                </div>

                {r.phone && (
                  <p className="mt-3 text-sm text-ink/75 flex items-center gap-2">
                    <Phone
                      size={13}
                      className="text-champagne"
                      strokeWidth={1.6}
                    />
                    <a
                      href={`tel:${r.phone}`}
                      className="hover:text-ink underline-offset-2 hover:underline"
                    >
                      {r.phone}
                    </a>
                  </p>
                )}

                {r.message && (
                  <div className="mt-3 flex items-start gap-2">
                    <MessageSquare
                      size={13}
                      className="text-champagne mt-0.5 shrink-0"
                      strokeWidth={1.6}
                    />
                    <p className="text-sm text-ink/70 italic font-serif leading-relaxed">
                      &ldquo;{r.message}&rdquo;
                    </p>
                  </div>
                )}
              </motion.article>
            ))}
          </AnimatePresence>
        </section>
      </main>
    </div>
  );
}

export default function AdminPage() {
  const [pw, setPw] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");

  if (!pw) {
    return <LoginGate onUnlock={setPw} />;
  }
  return <Dashboard password={pw} onLogout={() => setPw("")} />;
}
